// C:\\Users\\abdul\\Downloads\\Onebox\\onebox\\src\\components\\AboutUs.jsx
import React, { useState, useEffect } from 'react';
import { FaCheck } from 'react-icons/fa';
import { supabase } from '../supabaseClient';
import './AboutUs.css';

const AboutUs = ({ tenant = {} }) => {
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch about data from Supabase
  useEffect(() => {
    if (!tenant?.id) {
      setLoading(false);
      return;
    }

    fetchAboutData();

    // Subscribe to real-time updates
    const channel = supabase
      .channel('about-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'website_sections',
          filter: `tenant_id=eq.${tenant.id}`,
        },
        (payload) => {
          console.log('🔄 About section updated:', payload);
          if (payload.new?.section_name === 'about') {
            setAboutData(payload.new.content);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [tenant?.id]);

  const fetchAboutData = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('website_sections')
        .select('content, is_visible')
        .eq('tenant_id', tenant.id)
        .eq('section_name', 'about')
        .maybeSingle();

      if (error) {
        console.error('Error fetching about data:', error);
        setAboutData(getDefaultData());
      } else if (data && data.is_visible) {
        setAboutData(data.content);
        console.log('✅ About data loaded from database');
      } else {
        setAboutData(getDefaultData());
      }
    } catch (error) {
      console.error('Error in fetchAboutData:', error);
      setAboutData(getDefaultData());
    } finally {
      setLoading(false);
    }
  };

  // Default fallback data
  const getDefaultData = () => ({
    label: 'About us',
    title: 'We<|>Create the best foody product',
    description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Quisque diam pellentesque bibendum non dui volutpat fringilla bibendum. Urna, elit augue urna, vitae feugiat pretium donec id elementum. Ultrices mattis sed vitae mus risus. Lacus nisi, et ac dapibus sit eu velit in consequat.',
    features: [
      'Lacus nisi, et ac dapibus sit eu velit in consequat.',
      'Quisque diam pellentesque bibendum non dui volutpat fringilla',
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit'
    ],
    button_text: 'Read More',
    button_link: '#menu',
    images: [
      'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&q=80',
      'https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=400&q=80',
      'https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=400&q=80'
    ],
    is_active: true,
  });

  // Get data with fallbacks
  const data = aboutData || getDefaultData();
  const label = data?.label ? String(data.label) : 'About us';
  const title = data?.title ? String(data.title) : 'We Create the best foody product';
  const description = data?.description ? String(data.description) : 'Lorem ipsum dolor sit amet...';
  const features = Array.isArray(data?.features) ? data.features.filter(f => f && f.trim() !== '') : [];
  const buttonText = data?.button_text ? String(data.button_text) : 'Read More';
  const buttonLink = data?.button_link ? String(data.button_link) : '#menu';
  
  // Filter out empty images
  const images = Array.isArray(data?.images) 
    ? data.images.filter(img => img && img.trim() !== '')
    : [];

  // Get primary color from tenant
  const primaryColor = tenant?.primary_color || '#FF9F0D';

  // Parse title for highlighting (supports "text<|>highlighted text")
  const renderTitle = () => {
    if (title.includes('<|>')) {
      const parts = title.split('<|>');
      return (
        <>
          <span className="about-we" style={{ color: primaryColor }}>
            {parts[0]}
          </span>
          {parts[1]}
        </>
      );
    }
    return title;
  };

  const handleButtonClick = () => {
    if (buttonLink.startsWith('#')) {
      const element = document.querySelector(buttonLink);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.location.href = buttonLink;
    }
  };

  // Show loading state (optional - can remove if you want instant render)
  if (loading) {
    return (
      <section id="about" className="about-section">
        <div className="about-container">
          <div className="about-loading">
            <div className="spinner"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="about" className="about-section">
      <div className="about-container">
        {/* Left Side - Text Content */}
        <div className="about-content">
          <span 
            className="about-label"
            style={{ color: primaryColor }}
          >
            {label}
          </span>
          <h2 className="about-title">
            {renderTitle()}
          </h2>
          <p className="about-description">
            {description}
          </p>

          {/* Check List - Dynamic */}
          {features.length > 0 && (
            <ul className="about-list">
              {features.map((feature, index) => {
                const featureText = typeof feature === 'string' ? feature : String(feature);
                
                if (!featureText.trim()) return null;
                
                return (
                  <li key={`feature-${index}`} className="about-list-item">
                    <FaCheck className="check-icon" style={{ color: primaryColor }} />
                    <span>{featureText}</span>
                  </li>
                );
              })}
            </ul>
          )}

          {buttonText && (
            <button 
              className="about-button"
              style={{ backgroundColor: primaryColor }}
              onClick={handleButtonClick}
            >
              {buttonText}
            </button>
          )}
        </div>

        {/* Right Side - Image Grid - Dynamic */}
        <div className="about-images">
          {/* Large Image - Top */}
          {images[0] && (
            <div className="image-large">
              <img
                src={images[0]}
                alt={`${tenant?.business_name || 'Restaurant'} showcase`}
                className="about-img"
                loading="lazy"
              />
            </div>
          )}

          {/* Two Small Images - Bottom */}
          {(images[1] || images[2]) && (
            <div className="image-small-group">
              {images[1] && (
                <div className="image-small">
                  <img
                    src={images[1]}
                    alt={`${tenant?.business_name || 'Restaurant'} food`}
                    className="about-img"
                    loading="lazy"
                  />
                </div>
              )}
              {images[2] && (
                <div className="image-small">
                  <img
                    src={images[2]}
                    alt={`${tenant?.business_name || 'Restaurant'} meal`}
                    className="about-img"
                    loading="lazy"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AboutUs;

